import clsx from 'clsx'
import GridSpinner from 'components/UI/GridSpinner'
import { Button, Col, Container, Grid, Icon, Row, Typography } from 'gu-ui-kit'
import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { Link, useRouteMatch } from 'react-router-dom'
import formatMoney from 'utils/formatMoney'
import { useCancelToken } from 'utils/hooks/useCancelToken'
import useNetworkPalette from 'utils/hooks/useNetworkPalette'
import ActionLink from 'modules/Services/Account/subcomponents/ActionLink'
import servicesCommonStyles from 'modules/Services/services.module.scss'
import styles from 'modules/Services/Campaigns/Main/network-account-campaign.module.scss'
import List from './subcomponents/List'
import { ROUTES } from 'constants/routes'
import { useHistory } from 'react-router'
import { FETCH_NETWORK_ACCOUNT } from 'modules/Services/types'

const Account = () => {
  const {
    params: { networkType, accountId },
  } = useRouteMatch()
  const { t } = useTranslation(['services'])
  const { cancelableDispatch } = useCancelToken()
  const pathGen = useGeneratePathI18n()
  const history = useHistory()
  const {
    networkAccount: { login, status, balance, main_action },
    i18n,
  } = useSelector((state) => ({
    networkAccount: state['services/network-account'],
    i18n: state['app'].i18n,
  }))

  const paletteClass = useNetworkPalette(networkType)

  useEffect(() => {
    new Promise((resolve, reject) =>
      cancelableDispatch({
        type: FETCH_NETWORK_ACCOUNT,
        payload: { accountId },
        meta: { resolve, reject },
      }),
    ).catch(() => {
      history.push(
        pathGen(ROUTES.SERVICES_NETWORK_ACCOUNT, {
          networkType,
          accountId,
        }),
      )
    })
  }, [accountId])

  return (
    <>
      <Helmet defer={false}>
        <title>
          {t(`services::networks.${networkType}`)}
          {login ? ' —  ' + login : ''}
        </title>
      </Helmet>

      <Grid className={styles['network-account__grid-container']}>
        <Row justifyContent="center">
          <Col sm="12" lg="6" className={styles['network-account__column']}>
            <Button
              component={Link}
              to={pathGen(ROUTES.SERVICES_NETWORK, {
                networkType,
              })}
              startIcon={<Icon name={'back'} size={'l'} />}
              className={styles['network-account__back-button']}
            />
            <div className={styles['network-account__container']}>
              <div>
                <div
                  className={clsx(
                    'bg-network-' + networkType + '-static',
                    styles['network-account__head'],
                    servicesCommonStyles['palette_' + paletteClass],
                  )}
                >
                  <Link
                    to={pathGen(ROUTES.SERVICES_NETWORK, {
                      networkType,
                    })}
                  >
                    {t(`services::networks.${networkType}`)}
                  </Link>
                </div>

                <div className={styles['network-account__description']}>
                  {balance.amount === null ? (
                    <GridSpinner color={'inherit'} />
                  ) : (
                    <>
                      <div className={styles['network-account__account']}>
                        <Container
                          display={'flex'}
                          flexJustify={'space-between'}
                          flexAlign={'flex-start'}
                          flexWrap={'wrap'}
                        >
                          <div className={styles['network-account__login-container']}>
                            <Typography
                              variant={'Body-25'}
                              className={styles['network-account__login']}
                            >
                              {login}
                              {status === 'deleted' && (
                                <span className={styles['network-account__deleted-label']}>
                                  ( {t(`services::account_list.statuses.${status}`).toLowerCase()} )
                                </span>
                              )}
                            </Typography>
                          </div>
                        </Container>
                      </div>
                      <Typography
                        variant={'Header-70'}
                        component={
                          <span
                            dangerouslySetInnerHTML={{
                              __html: formatMoney(balance.amount, balance.currency),
                            }}
                          />
                        }
                      />
                    </>
                  )}

                  <Container display={'flex'} flexWrap={'wrap'}>
                    {!!main_action && (
                      <ActionLink
                        action={main_action}
                        i18n={i18n}
                        id={accountId}
                        type={networkType}
                        balance={balance}
                      />
                    )}

                    {main_action && networkType === 'yandex-business' && (
                      <ActionLink
                        action={'campaign-create'}
                        i18n={i18n}
                        id={accountId}
                        type={networkType}
                      />
                    )}
                  </Container>
                </div>
              </div>

              <List accountId={accountId} networkType={networkType} />
            </div>
          </Col>
        </Row>
      </Grid>
    </>
  )
}

export default Account
